// lib
import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import {
  renderMarketSimulatorProps,
  renderMarketSimulatorTableProps
} from '@aytm/app/stats/page/question-views/cjc/lib/table-utils'

// components
import { Checkbox } from 'primereact/checkbox'

import Table from '@aytm/app/stats/components/data-views/table/components/Table'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/cjc/components/market-simulator/styles/MarketSimulator.module.scss'

const MAX_PACKAGES_AMOUNT = 7

const EXPORT_OPTIONS = [{ text: 'Download Market Simulator Excel', key: 'marketSimulator' }]

const MarketSimulator = ({
  id,
  features,
  answer,
  batches,
  mode,
  persona,
  marketSimulatorPackages,
  marketSimulatorNA,
  onNAChange,
  onExport,
  addMarketSimulatorPackage,
  deleteMarketSimulatorPackage,
  setMarketSimulatorPackage
}) => {
  // a refetch can hand over an answer that has not got the field yet; it reads as no None option, as
  // the report params already read it, rather than throwing and taking the report down
  const hasNA = useMemo(() => Boolean(answer?.hasNA?.[0]), [answer?.hasNA])

  const { packages, percents, naPercent } = useMemo(
    () => renderMarketSimulatorProps(answer, mode, persona, marketSimulatorPackages, marketSimulatorNA),
    [answer, mode, persona, JSON.stringify(marketSimulatorPackages), marketSimulatorNA]
  )

  const tableProps = useMemo(
    () =>
      renderMarketSimulatorTableProps(
        batches,
        packages,
        percents,
        deleteMarketSimulatorPackage,
        setMarketSimulatorPackage
      ),
    [batches, packages, percents, deleteMarketSimulatorPackage, setMarketSimulatorPackage]
  )

  const onExportClick = (fileType, callback) => {
    onExport(
      {
        fileType,
        exportType: 'marketSimulator'
      },
      callback
    )
  }

  return (
    <div className={style.component}>
      <div className={classNames(style.row, style['row-header'])}>
        <span className={style['row-header-title']}>MARKET SIMULATOR - compare any combinations:</span>

        {/* only an authorized viewer downloads the simulator, never a share-link guest */}
        {features.export.disabled || features.authorized_exports.disabled ? null : (
          <div className="p-right">
            <ExportDropdown options={EXPORT_OPTIONS} onExport={onExportClick} />
          </div>
        )}
      </div>

      <Table {...tableProps} />

      {hasNA ? (
        <div className={classNames(style.row, style['row-na'])}>
          <div className="p-col-12">
            <Checkbox
              className="p-checkbox-sm"
              aria-labelledby={`${id}-market-simulator-na-label`}
              checked={marketSimulatorNA}
              onChange={() => onNAChange(!marketSimulatorNA)}
            />
            <span
              id={`${id}-market-simulator-na-label`}
              className="p-checkbox-label p-checkbox-label-sm"
              onClick={() => onNAChange(!marketSimulatorNA)}
            >
              N/A{marketSimulatorNA ? <>&nbsp;&nbsp;&nbsp;{naPercent.toFixed(1)}%</> : ''}
            </span>
          </div>
        </div>
      ) : null}

      {packages.length < MAX_PACKAGES_AMOUNT ? (
        <div className={style.row}>
          <span className={style['row-add']} {...pressable(addMarketSimulatorPackage)}>
            + ADD SELECTED ({MAX_PACKAGES_AMOUNT - packages.length} left)
          </span>
        </div>
      ) : null}
    </div>
  )
}

MarketSimulator.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  features: PropTypes.object,
  answer: answerPropType,
  batches: PropTypes.array,
  mode: PropTypes.string,
  persona: PropTypes.number,
  packages: PropTypes.array,
  marketSimulatorPackages: PropTypes.array,
  marketSimulatorNA: PropTypes.bool,
  onNAChange: PropTypes.func,
  onExport: PropTypes.func,
  addMarketSimulatorPackage: PropTypes.func,
  deleteMarketSimulatorPackage: PropTypes.func,
  setMarketSimulatorPackage: PropTypes.func
}

MarketSimulator.displayName = 'MarketSimulator'

export default MarketSimulator
